// utils/filterVisibilityHelpers.js

import { constants } from '../constants/Constants';

/**
 * Configuration object for sector filter visibility rules
 * Each rule defines when the sector filter should be hidden
 */
const SECTOR_FILTER_EXCLUSIONS = {
  // Exclude by model ID
  modelExclusions: [
    constants.modals.productPairing,
    constants.modals.shareWise,
  ],

  // Exclude by program ID
  programExclusions: [
    constants.programs.Automation,
    constants.programs.GPG,
    constants.programs.RGM,
  ],

  // Exclude by product ID
  productExclusions: [
    constants.products.Canvas,
    constants.products.TPO,
    constants.products.ShouldCostModelling,
  ],
};

/**
 * Configuration object for market filter visibility rules
 */
const MARKET_FILTER_EXCLUSIONS = {
  // Exclude by model ID
  modelExclusions: [
    constants.modals.productPairing,
    constants.modals.shareWise,
  ],

  // Exclude by product ID
  productExclusions: [
    constants.products.Canvas,
    constants.products.Archlet,
    constants.products.ShouldCostModelling,
    constants.products.ManufacturingControlTower,
    constants.products.ProfitToServe,
    constants.products.TPO,
  ],
};

/**
 * Determines if the sector filter should be visible
 * @param {Object} selectedModel - The currently selected model
 * @param {Object} selectedProgram - The currently selected program
 * @param {Object} selectedProduct - The currently selected product
 * @param {Array} sectorOptions - Available sector options
 * @returns {boolean} - True if sector filter should be visible
 */
export const shouldShowSectorFilter = (
  selectedModel,
  selectedProgram,
  selectedProduct,
  sectorOptions = []
) => {
  // Return false if no sector options available
  if (!sectorOptions.length) {
    return false;
  }

  // Check model exclusions
  if (
    selectedModel?.id &&
    SECTOR_FILTER_EXCLUSIONS.modelExclusions.includes(selectedModel.id)
  ) {
    return false;
  }

  // Check program exclusions
  if (
    selectedProgram?.programId &&
    SECTOR_FILTER_EXCLUSIONS.programExclusions.includes(
      selectedProgram.programId
    )
  ) {
    return false;
  }

  // Check product exclusions
  return !(
    selectedProduct?.productId &&
    SECTOR_FILTER_EXCLUSIONS.productExclusions.includes(
      selectedProduct.productId
    )
  );
};

/**
 * Determines if the market filter should be visible
 * @param {Object} selectedModel - The currently selected model
 * @param {Object} selectedProduct - The currently selected product
 * @param {Array} marketOptions - Available market options
 * @returns {boolean} - True if market filter should be visible
 */
export const shouldShowMarketFilter = (
  selectedModel,
  selectedProduct,
  marketOptions = []
) => {
  // Return false if no market options available
  if (!marketOptions.length) {
    return false;
  }

  // Check model exclusions
  if (
    selectedModel?.id &&
    MARKET_FILTER_EXCLUSIONS.modelExclusions.includes(selectedModel.id)
  ) {
    return false;
  }

  // Check product exclusions
  return !(
    selectedProduct?.productId &&
    MARKET_FILTER_EXCLUSIONS.productExclusions.includes(
      selectedProduct.productId
    )
  );
};

/**
 * Determines if the sector filter should be visible in mobile view
 * This function uses string-based checks for legacy compatibility
 * @param {string} selectedProductValue - The selected product value as string
 * @param {string} selectedProgramValue - The selected program value as string
 * @param {Object} selectedProgram - The currently selected program object
 * @param {Object} selectedProduct - The currently selected product object
 * @param {Array} sectorOptions - Available sector options
 * @returns {boolean} - True if sector filter should be visible in mobile view
 */
export const shouldShowSectorFilterMobile = (
  selectedProductValue,
  selectedProgramValue,
  selectedProgram,
  selectedProduct,
  sectorOptions = []
) => {
  // Return false if no sector options available
  if (!sectorOptions.length) {
    return false;
  }

  // Check string-based exclusions for mobile view
  const isProductExcluded =
    selectedProductValue === 'Automation' ||
    selectedProductValue === 'Canvas' ||
    selectedProductValue === 'Should Cost Modelling';

  const isProgramExcluded =
    selectedProgramValue === 'RGM' ||
    selectedProgram?.programId === constants?.programs?.GPG;

  const isTPOProduct = selectedProduct?.productId === constants.products.TPO;

  // Return false if any exclusion condition is met
  return !(isProductExcluded || isProgramExcluded || isTPOProduct);
};

/**
 * Generic helper to check if a value is in an exclusion list
 * @param {any} value - The value to check
 * @param {Array} exclusionList - The list of excluded values
 * @returns {boolean} - True if value is excluded
 */
export const isExcluded = (value, exclusionList = []) => {
  return exclusionList.includes(value);
};

/**
 * Updates the exclusion configuration if needed
 * This allows for easy maintenance of the exclusion rules
 */
export const updateFilterExclusions = {
  /**
   * Add a new sector exclusion rule
   * @param {string} type - Type of exclusion ('model', 'program', 'product')
   * @param {number|Array} value - The value(s) to exclude
   */
  addSectorExclusion: (type, value) => {
    const exclusionKey = `${type}Exclusions`;
    if (SECTOR_FILTER_EXCLUSIONS[exclusionKey]) {
      const values = Array.isArray(value) ? value : [value];
      SECTOR_FILTER_EXCLUSIONS[exclusionKey].push(...values);
    }
  },

  /**
   * Add a new market exclusion rule
   * @param {string} type - Type of exclusion ('model', 'product')
   * @param {number|Array} value - The value(s) to exclude
   */
  addMarketExclusion: (type, value) => {
    const exclusionKey = `${type}Exclusions`;
    if (MARKET_FILTER_EXCLUSIONS[exclusionKey]) {
      const values = Array.isArray(value) ? value : [value];
      MARKET_FILTER_EXCLUSIONS[exclusionKey].push(...values);
    }
  },
};

/**
 * Determines whether the site filter should be displayed
 *
 * @param {Object} selectedProgram - The currently selected program
 * @param {Array} plantOptions - Available plant/site options
 * @returns {boolean} - Whether the site filter should be displayed
 */
export const shouldShowSiteFilter = (selectedProgram, plantOptions) => {
  return (
    selectedProgram?.programId === constants?.programs?.Manufacturing &&
    Array.isArray(plantOptions) &&
    plantOptions.length > 0
  );
};

// Export the configurations for direct access if needed
export { SECTOR_FILTER_EXCLUSIONS, MARKET_FILTER_EXCLUSIONS };
